import json5 from "json5";

import type { Language } from "src/components/editor";
import { v10Engine } from "src/utils/liquid-v10-engine";

export type LiquidPreviewResult = {
  output: string | null;
  error: string | null;
};

export type LiquidPreviewScope = {
  row: Record<string, unknown>;
  rows: Record<string, unknown>[];
};

/**
 * Renders a Liquid template against sample data using the shared client-side
 * `v10Engine`, returning the result as a string for read-only preview.
 *
 * Language-agnostic: works for any `Editor` language. JSON gets pretty-printed
 * as a convenience (parsed with json5 so trailing commas / unquoted keys still
 * format), every other language is returned as rendered.
 *
 * Both `row` and `rows` are provided in scope so single-row (`{{ row.x }}`) and
 * batch (`{% for row in rows %}`) templates resolve without a batch flag.
 */
export function renderLiquidPreview(
  template: string,
  scope: LiquidPreviewScope,
  language?: Language,
): LiquidPreviewResult {
  try {
    const rendered = String(v10Engine.parseAndRenderSync(template, scope));
    return { output: formatRendered(rendered, language), error: null };
  } catch (e) {
    return { output: null, error: (e as Error).message };
  }
}

function formatRendered(text: string, language?: Language): string {
  if (language !== "json") {
    return text;
  }
  try {
    return JSON.stringify(json5.parse(text), null, 2);
  } catch {
    return text;
  }
}
